import { IPaginatedData } from "./common";

export type BreakdownId = string;

export enum BreakdownStatus {
  Draft = 1,
  Active,
  Archived,
}

export enum BreakdownFilterStatus {
  Draft = BreakdownStatus.Draft,
  Active = BreakdownStatus.Active,
  Archived = BreakdownStatus.Archived,
}

export interface IBreakdownListItemRole {
  id: string;
  name: string;
  sequence: number;
}

export interface IBreakdownListItem {
  id: BreakdownId;
  title: string;
  subject: string;
  productionTypeId: number;
  projectId: string;
  projectName: string;
  sent: string;
  created: string;
  closingDate: string;
  status: BreakdownStatus;
  rolesCount: number;
  suggestionCount?: number;
  linkmailType: LinkmailType;
  amendmentCount: number;
  amendmentParentId: string | null;
  hiddenBySpotlight?: boolean;
  roles: IBreakdownListItemRole[];
  agentListIds: number[];
  agentCategories: AgentCategory[];
  performerCategories: PerformerCategory[];
  internationalDistributionPartners: InternationalDistributionPartner[];
}

export interface IPaginatedBreakdownList
  extends IPaginatedData<IBreakdownListItem> {}

export interface IBreakdownDetails {
  subject: string;
  castingCountryCode: string | null;
  castingClosingDate: string;
  castingLocation: string;
  castingDetails: string;
  concurrencyToken?: string;
  castingLocations: { id: string; name: string }[];
  castingTypeOtherDetails: string;
  production: {
    typeId: number;
    title: string;
    location: string;
    locationId?: string;
    countryCode: string | null;
    contractTypeId: number;
    summary: string;
    dates: string;
    director: string;
    producer: string;
    company: string;
    castingDirector: string;
    castingDirectorId?: string;
  };
}

export interface IBreakdownLinkRole {
  linkmailId: string;
  roleId: string;
}

export interface IBreakdownLinkRoles {
  links: IBreakdownLinkRole[];
  concurrencyToken?: string;
}

export interface IBreakdownRoles {
  roles: IBreakdownRole[];
  concurrencyToken?: string;
}

export interface IBreakdownRole {
  role: IRole;
  sequence: number;
}

export enum DisabilityPreference {
  Undefined,
  Any,
  Specific,
  None,
}

export interface IRole {
  id: string;
  name: string;
  description: string;
  anyGender: boolean;
  genderIds: number[];
  genderInfo: string;
  disabilityPreference: DisabilityPreference;
  disabilityIds: number[];
  disabilityInfo: string;
  minimumPlayingAge: number | null;
  maximumPlayingAge: number | null;
  minimumHeight: number | null;
  maximumHeight: number | null;
  agreementAccepted: boolean;
  anyAppearance: boolean;
  appearanceIds: number[];
  contractTypeId: number;
  contractTypeInfo: string;
  linkAttachments: ILinkAttachment[];
  isCast?: boolean | null;
  earlyApplyLimit?: number;
  anyEthnicity: boolean;
  ethnicityLabel: string;
}

export enum AgentCategory {
  AgentsAndPersonalManagers = 0,
  YoungPerformersAgents = 1,
  PresentersAgents = 2,
  VoiceOverAgents = 3,
  DanceAgents = 4,
}

export enum InternationalDistributionPartner {
  NewYork = 0,
  LosAngeles = 1,
  Toronto = 2,
  Vancouver = 3,
  Australia = 4,
  NewZealand = 5,
  Sweden = 6,
  Norway = 7,
  Germany = 8,
  Austria = 9,
  Switzerland = 10,
  France = 11,
}

export enum PerformerCategory {
  Unknown = 0,
  ActorActress = 1,
  GraduateMale = 18,
  GraduateFemale = 19,
  PresenterMale = 6,
  PresenterFemale = 7,
  Dancer = 24,
}

export enum IncludePersonalDetails {
  None = 0,
  NameOnly = 1,
  NameAndEmail = 2,
}

export interface IBreakdownSendOptions {
  message: string;
  includePersonalDetailsToPerformers: IncludePersonalDetails;
  includePersonalDetailsToAgents: IncludePersonalDetails;
  spotlightExclusive: boolean;
  agentCategories: AgentCategory[];
  performerCategories: PerformerCategory[];
  internationalDistributionPartners: InternationalDistributionPartner[];
  concurrencyToken?: string;
  linkAttachments: ILinkAttachment[];
  agentListIds: number[];
}

export interface ILinkAttachment {
  attachmentId: string;
  originalFileName: string;
  url: string;
  sequence: number;
}

export interface IBreakdown
  extends IBreakdownDetails,
    IBreakdownRoles,
    IBreakdownSendOptions {
  id: BreakdownId;
  message: string;
  created: string;
  sent: string;
  dateUpserted: string;
  freezeDate?: string | null;
  projectName: string;
  sentBy: string;
  linkmailType: LinkmailType;
  amendmentParentId: string | null;
  amendmentCount: number;
  hiddenBySpotlight?: boolean;
  isLocalHire?: boolean;
  castingTypes: number[];
  closedEarly?: boolean;
  closedReason?: string;
}

export interface IRelatedRole {
  id: string;
  name: string;
}

export interface IRelatedBreakdownRoles {
  id: string;
  title: string;
  linkmailType: LinkmailType;
  amendmentCount: number;
  roles: IRelatedRole[];
}

export enum LinkmailType {
  Default = 1,
  Amendment = 2,
  Duplicate = 3,
}
